import React from 'react';
import PropTypes from 'prop-types';

class AlbumPlayer extends React.PureComponent {
  static propTypes = {
    tracks: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      artist: PropTypes.string,
      file: PropTypes.string,
      url: PropTypes.string,
      duration: PropTypes.number,
      cover: PropTypes.string
    })).isRequired,
    artist: PropTypes.string,
    skin: PropTypes.string,
    className: PropTypes.string,
    onReady: PropTypes.func
  };

  static defaultProps = {
    artist: 'Unknown Artist',
    skin: null,
    className: null,
    onReady: null
  };

  constructor (props) {
    super(props);
    this.syncContainerSize = this.syncContainerSize.bind(this);
    this.resetContainerSize = this.resetContainerSize.bind(this);
    this.normalizeWebampLayout = this.normalizeWebampLayout.bind(this);
  }

  containerRef = React.createRef();

  componentDidMount () {
    this._isMounted = true;
    this.loadPlayer();
    window.addEventListener('resize', this.syncContainerSize);
  }

  componentDidUpdate (prevProps) {
    const prevSig = this.getTracksSignature(prevProps.tracks);
    const nextSig = this.getTracksSignature(this.props.tracks);
    if (prevSig !== nextSig || prevProps.skin !== this.props.skin) {
      this.disposePlayer();
      this.loadPlayer();
    }
  }

  componentWillUnmount () {
    this._isMounted = false;
    this.disposePlayer();
    window.removeEventListener('resize', this.syncContainerSize);
  }

  async loadPlayer () {
    if (typeof window === 'undefined' || !this.containerRef.current) {
      return;
    }

    const { default: Webamp } = await import('webamp');
    if (!this._isMounted) return;

    const { tracks, artist, skin, onReady } = this.props;
    const playableTracks = tracks.filter(track => track.file || track.url);

    if (!playableTracks.length) {
      return;
    }

    const initialTracks = playableTracks.map(track => ({
      metaData: track.metaData || {
        artist: track.artist || artist,
        title: track.title,
        album: track.albumTitle
      },
      defaultName: track.defaultName || `${track.artist || artist || 'Unknown Artist'} - ${track.title}`,
      url: track.url || track.file || '',
      duration: track.duration
    }));

    if (typeof Webamp.browserIsSupported === 'function' && !Webamp.browserIsSupported()) {
      return;
    }

    const mainWindowWidth = 275;
    const equalizerWidth = 275;
    const playlistWindowWidth = 375;
    const mainWindowHeight = 116;
    const windowSpacing = 12;

    const mainY = 0;
    const mainX = 0;
    // Arrange all windows on a single row: main | equalizer | playlist
    const equalizerY = mainY;
    const equalizerX = mainX + mainWindowWidth + windowSpacing;
    const playlistY = mainY;
    const playlistX = equalizerX + equalizerWidth + windowSpacing;

    this.webamp = new Webamp({
      initialTracks,
      __initialState: {
        display: {
          doubled: false
        }
      },
      __initialWindowLayout: {
        main: {
          position: { x: mainX, y: mainY }
        },
        playlist: {
          size: [4, 0],
          position: { x: playlistX, y: playlistY }
        },
        equalizer: {
          position: { x: equalizerX, y: equalizerY }
        }
      },
      ...(skin ? { initialSkin: { url: skin } } : {})
    });

    try {
      await this.webamp.renderWhenReady(this.containerRef.current);
      const webampEl = document.getElementById('webamp');
      if (webampEl) {
        const container = this.containerRef.current;
        const totalWidth = (playlistX - mainX) + playlistWindowWidth;
        const totalHeight = mainWindowHeight;

        if (container) {
          if (webampEl.parentElement !== container) {
            container.appendChild(webampEl);
          }
          container.style.overflow = 'visible';
          container.style.position = 'relative';
          container.style.display = 'block';
          container.style.margin = '0 auto';
        }
        webampEl.style.position = 'relative';
        webampEl.style.top = 'auto';
        webampEl.style.left = 'auto';
        webampEl.style.margin = '0';
        webampEl.style.display = 'block';
        webampEl.style.overflow = 'visible';
        webampEl.style.transformOrigin = 'top left';
        webampEl.style.transform = 'none';
        this.normalizeWebampLayout();
        setTimeout(this.normalizeWebampLayout, 0);
        setTimeout(this.normalizeWebampLayout, 300);
      }
      if (this._isMounted && typeof onReady === 'function') {
        onReady(this.webamp);
      }
    } catch (error) {
      console.error('[AlbumPlayer] Failed to render Webamp', error);
    }
  }

  disposePlayer () {
    if (this.webamp) {
      const webampInstance = this.webamp;
      this.webamp = null;
      setTimeout(() => {
        try {
          if (webampInstance && typeof webampInstance.dispose === 'function') {
            webampInstance.dispose();
          }
        } catch (err) {
          // In dev, Webamp may have been reparented/cleaned by HMR. Swallow.
          // eslint-disable-next-line no-console
          console.warn('[AlbumPlayer] Webamp dispose issue (non-fatal):', err && err.message ? err.message : err);
        }
      }, 0);
      const { onReady } = this.props;
      if (typeof onReady === 'function') {
        onReady(null);
      }
      this.resetContainerSize();
    }
  }

  syncContainerSize () {
    if (!this.containerRef.current) return;
    const webampEl = this.containerRef.current.querySelector('#webamp');
    if (!webampEl) return;

    this.normalizeWebampLayout();
  }

  resetContainerSize () {
    if (!this.containerRef.current) return;
    this.containerRef.current.style.width = '';
    this.containerRef.current.style.height = '';
  }

  getTracksSignature (tracks) {
    if (!Array.isArray(tracks)) return '';
    return tracks
      .map(track => `${track.url || track.file || ''}::${track.title || ''}`)
      .join('|');
  }

  normalizeWebampLayout () {
    if (!this.containerRef.current) return;
    const webampEl = this.containerRef.current.querySelector('#webamp');
    if (!webampEl || typeof webampEl.getBoundingClientRect !== 'function') return;

    const parseTranslate = value => {
      if (!value) return { x: 0, y: 0 };
      const translateMatch = value.match(/translate\(([-0-9.]+)px,\s*([-0-9.]+)px\)/);
      if (translateMatch) {
        return { x: parseFloat(translateMatch[1]), y: parseFloat(translateMatch[2]) };
      }
      const matrixMatch = value.match(/matrix\(([-0-9.]+),\s*[-0-9.]+,\s*[-0-9.]+,\s*[-0-9.]+,\s*([-0-9.]+),\s*([-0-9.]+)\)/);
      if (matrixMatch) {
        return { x: parseFloat(matrixMatch[2]), y: parseFloat(matrixMatch[3]) };
      }
      return { x: 0, y: 0 };
    };

    const findWrapper = win => {
      let node = win;
      while (node && node !== webampEl) {
        const hasTranslate = node.style && /translate\(/.test(node.style.transform || '');
        const isAbsolute = node.style && node.style.position === 'absolute';
        if (hasTranslate && isAbsolute) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    };

    const mainWindowWidth = 275;
    const equalizerWidth = 275;
    const playlistWindowWidth = 375;
    const mainWindowHeight = 116;

    const mainWrapper = findWrapper(webampEl.querySelector('#main-window'));
    const eqWrapper = findWrapper(webampEl.querySelector('#equalizer-window'));
    const playlistWrapper = findWrapper(webampEl.querySelector('#playlist-window'));

    const wrappers = [
      { wrapper: mainWrapper, width: mainWindowWidth, height: mainWindowHeight },
      { wrapper: eqWrapper, width: equalizerWidth, height: mainWindowHeight },
      { wrapper: playlistWrapper, width: playlistWindowWidth, height: mainWindowHeight }
    ].filter(item => item.wrapper);

    if (!wrappers.length) return;

    let minOffsetX = Infinity;
    let maxOffsetX = -Infinity;

    const offsets = wrappers.map(({ wrapper, width }) => {
      const offset = parseTranslate(wrapper.style && wrapper.style.transform);
      minOffsetX = Math.min(minOffsetX, offset.x);
      maxOffsetX = Math.max(maxOffsetX, offset.x + width);
      return { wrapper, width, x: offset.x };
    });

    if (!isFinite(minOffsetX) || !isFinite(maxOffsetX)) {
      return;
    }

    const normalizedWidth = Math.max(0, Math.round(maxOffsetX - minOffsetX));
    const normalizedHeight = mainWindowHeight;

    offsets.forEach(({ wrapper, x }) => {
      const nextX = Math.round(x - minOffsetX);
      wrapper.style.transform = `translate(${nextX}px, 0px)`;
    });

    webampEl.style.transform = 'none';
    webampEl.style.width = `${normalizedWidth}px`;
    webampEl.style.height = `${normalizedHeight}px`;
    this.containerRef.current.style.width = `${normalizedWidth}px`;
    this.containerRef.current.style.height = `${normalizedHeight}px`;
  }

  render () {
    const { className } = this.props;

    return (
      <div
        className={className}
        ref={this.containerRef}
      />
    );
  }
}

export { AlbumPlayer };
